import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Sparkles, ListChecks, BookOpen, Gift, CalendarDays, MessageCircle, Heart, ArrowRight } from "lucide-react";

export function Landing() {
  const navigate = useNavigate();

  const features = [
    { icon: ListChecks, title: "Track Chores", desc: "Set up chores with schedules, timers, and grace periods", color: "text-app-success", bg: "bg-app-success/10" },
    { icon: BookOpen, title: "Household Rules", desc: "Establish rules with streak tracking and accountability", color: "text-app-info", bg: "bg-app-info/10" },
    { icon: Gift, title: "Rewards & Points", desc: "Fun rewards to earn and playful punishments to give", color: "text-app-accent", bg: "bg-app-accent/10" },
    { icon: CalendarDays, title: "Sessions", desc: "Schedule sessions to redeem points together", color: "text-app-secondary", bg: "bg-app-secondary/10" },
    { icon: MessageCircle, title: "Encrypted Chat", desc: "Private end-to-end encrypted messaging", color: "text-app-primary", bg: "bg-app-primary/10" },
  ];

  return (
    <div className="min-h-screen bg-app-bg page-enter">
      {/* Hero */}
      <div className="flex flex-col items-center justify-center px-4 pt-16 pb-12 md:pt-24 md:pb-16 text-center relative overflow-hidden">
        {/* Decorative gradient blobs */}
        <div className="absolute top-0 left-1/4 w-72 h-72 bg-app-primary/10 rounded-full blur-3xl -translate-y-1/2" />
        <div className="absolute top-10 right-1/4 w-60 h-60 bg-app-secondary/10 rounded-full blur-3xl" />

        <div className="relative z-10">
          <div className="mb-6 text-6xl sparkle">✨</div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-app-text mb-3 tracking-tight">
            Chore<span className="text-app-primary">App</span>
          </h1>
          <p className="text-body text-app-text-muted max-w-lg mb-8 leading-relaxed">
            A playful way for couples to track chores, build habits, and keep each other accountable — with fun rewards and punishments! 💕
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Button
              size="lg"
              className="bg-gradient-primary text-white px-8 py-3 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all btn-press"
              onClick={() => navigate("/register")}
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Get Started
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="max-w-3xl mx-auto px-4 pb-16">
        <h2 className="text-center text-lg font-bold text-app-text-muted mb-6 flex items-center justify-center gap-2">
          <Heart className="w-4 h-4 text-app-secondary" />
          Everything you need
          <Heart className="w-4 h-4 text-app-secondary" />
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {features.map((f) => (
            <div
              key={f.title}
              className="flex flex-col items-center text-center p-4 md:p-6 rounded-xl bg-app-surface border border-app-border card-hover cursor-default"
            >
              <div className={`w-12 h-12 rounded-full ${f.bg} flex items-center justify-center mb-3`}>
                <f.icon className={`w-6 h-6 ${f.color}`} />
              </div>
              <h3 className="text-body font-bold mb-1">{f.title}</h3>
              <p className="text-info text-app-text-muted">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="text-center pb-8 text-info text-app-text-muted">
        Made with <Heart className="w-3 h-3 inline text-app-secondary fill-app-secondary" /> for couples
      </div>
    </div>
  );
}
